import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Button,
  Chip, TextField, InputAdornment, Tabs, Tab, IconButton, Tooltip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Avatar, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CampaignIcon from '@mui/icons-material/Campaign';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';
import { renderTemplateBody, buildReferralLink } from '../../data/contentTemplates';

const PRIMARY = '#1B365D';

const TYPE_OPTIONS = [
  { key: 'brochure', label: 'Brochure', icon: PictureAsPdfIcon, color: '#1B365D' },
  { key: 'poster', label: 'Poster', icon: ImageIcon, color: '#7B1FA2' },
  { key: 'guide', label: 'Guide', icon: DescriptionIcon, color: '#00B894' },
  { key: 'banner', label: 'Banner', icon: ImageIcon, color: '#E65100' },
  { key: 'template', label: 'Template', icon: DescriptionIcon, color: '#546E7A' },
  { key: 'handbook', label: 'Handbook', icon: DescriptionIcon, color: '#1565C0' },
];

export default function PartnerMarketing() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const bump = () => setRefreshKey((k) => k + 1);

  const referralLink = useMemo(() => buildReferralLink(user), [user]);

  // Live materials filtered by audience + date + status
  const materials = useMemo(() => {
    if (!user?.id) return [];
    return db.marketingMaterials.getForUser(user.id, new Date());
  }, [user, refreshKey]);

  const types = useMemo(() => ['All', ...TYPE_OPTIONS.map((t) => t.label)], []);

  const filtered = useMemo(() => {
    return materials.filter((m) => {
      const typeLabel = TYPE_OPTIONS.find((t) => t.key === m.type)?.label || m.type;
      if (tab > 0 && typeLabel !== types[tab]) return false;
      if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [materials, tab, search, types]);

  // ─── Handlers ────────────────────────────────────────────
  const openShareDialog = (m) => {
    setSelectedMaterial(m);
    setShareDialogOpen(true);
  };

  const openPreview = (m) => {
    setSelectedMaterial(m);
    setPreviewDialogOpen(true);
  };

  const renderedContent = useMemo(() => {
    if (!selectedMaterial?.content) return '';
    return renderTemplateBody(selectedMaterial.content, referralLink);
  }, [selectedMaterial, referralLink]);

  const trackShare = (channel) => {
    if (!selectedMaterial) return;
    db.engine.simulateMaterialShare(selectedMaterial.id, user);
    db.auditLogs.create({
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      action: 'share_material',
      entityType: 'marketing_material',
      entityId: selectedMaterial.id,
      description: `Shared "${selectedMaterial.name}" via ${channel}`,
    });
    bump();
  };

  const handleCopyContent = () => {
    if (!renderedContent) return;
    navigator.clipboard?.writeText(renderedContent).catch(() => {});
    trackShare('copy');
    showSnack('Content copied to clipboard');
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(renderedContent);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    trackShare('whatsapp');
  };

  const openFacebook = () => {
    const url = encodeURIComponent(referralLink);
    const quote = encodeURIComponent(renderedContent.split('\n')[0] || selectedMaterial?.name || '');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, '_blank');
    trackShare('facebook');
  };

  const openTelegram = () => {
    const url = encodeURIComponent(referralLink);
    const text = encodeURIComponent(renderedContent);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
    trackShare('telegram');
  };

  const openEmail = () => {
    const subject = encodeURIComponent(selectedMaterial?.name || 'iMoney');
    const body = encodeURIComponent(renderedContent);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
    trackShare('email');
  };

  const handleDownload = (m) => {
    db.marketingMaterials.incrementDownloadCount(m.id);
    db.auditLogs.create({
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      action: 'download_material',
      entityType: 'marketing_material',
      entityId: m.id,
      description: `Downloaded "${m.name}"`,
    });
    showSnack(`Downloaded "${m.name}"`);
    bump();
  };

  // ─── Render ──────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>Marketing Materials</Typography>
          <Typography variant="body2" color="text.secondary">
            Share approved content with your customers — your referral link is automatically embedded
          </Typography>
        </Box>
        <TextField
          size="small" placeholder="Search materials..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      {/* Referral link banner */}
      <Alert icon={<CampaignIcon />} severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        Your referral link <strong>{referralLink}</strong> will be embedded in every shared material.
        Customers who apply via this link will be credited to your account.
      </Alert>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        {types.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* Grid */}
      {filtered.length > 0 ? (
        <Grid container spacing={3}>
          {filtered.map((item) => {
            const typeMeta = TYPE_OPTIONS.find((t) => t.key === item.type) || TYPE_OPTIONS[0];
            const TypeIcon = typeMeta.icon;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card sx={{
                  borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }, transition: 'all 0.2s',
                }}>
                  {/* Preview banner — show uploaded image or type icon fallback */}
                  <Box sx={{
                    height: 160, backgroundColor: item.imageUrl ? '#000' : `${typeMeta.color}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderBottom: '1px solid #E0E0E0', overflow: 'hidden',
                  }}>
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <TypeIcon sx={{ fontSize: 44, color: typeMeta.color, opacity: 0.6 }} />
                    )}
                  </Box>
                  <CardContent sx={{ p: 2.5, flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <Chip label={typeMeta.label} size="small"
                        sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}>
                      {item.name}
                    </Typography>
                    {(item.startDate || item.endDate) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Promo: {item.startDate || '—'} → {item.endDate || '∞'}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        ⬇ {item.downloadCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ↗ {item.shareCount || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 2.5, pb: 2, pt: 0, gap: 0.5 }}>
                    <Button size="small" variant="contained" startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openShareDialog(item)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem',
                        backgroundColor: PRIMARY, flex: 1 }}>
                      Share
                    </Button>
                    <Tooltip title="Preview"><IconButton size="small" onClick={() => openPreview(item)}>
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton></Tooltip>
                    <Tooltip title="Download"><IconButton size="small" onClick={() => handleDownload(item)}>
                      <DownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton></Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No marketing materials assigned to you yet. Contact admin to get materials.
        </Alert>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Share Material</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedMaterial?.name}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Your referral link is automatically embedded. When customers apply via the link, they're credited to you.
          </Alert>
          {selectedMaterial?.imageUrl && (
            <Box sx={{ mb: 2, textAlign: 'center', backgroundColor: '#000', borderRadius: 2, overflow: 'hidden' }}>
              <img
                src={selectedMaterial.imageUrl}
                alt={selectedMaterial.name}
                style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }}
              />
            </Box>
          )}
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
            Content Preview
          </Typography>
          <Box sx={{
            p: 2, mb: 2, backgroundColor: '#F5F7FA', borderRadius: 2,
            whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem',
            maxHeight: 240, overflowY: 'auto', border: '1px solid #E0E0E0',
          }}>
            {renderedContent || '(no content)'}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5 }}>
            Share via
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Tooltip title="WhatsApp">
              <IconButton onClick={openWhatsApp}
                sx={{ backgroundColor: '#25D36615', '&:hover': { backgroundColor: '#25D36625' } }}>
                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 28 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Facebook">
              <IconButton onClick={openFacebook}
                sx={{ backgroundColor: '#1877F215', '&:hover': { backgroundColor: '#1877F225' } }}>
                <FacebookIcon sx={{ color: '#1877F2', fontSize: 28 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Telegram">
              <IconButton onClick={openTelegram}
                sx={{ backgroundColor: '#0088CC15', '&:hover': { backgroundColor: '#0088CC25' } }}>
                <TelegramIcon sx={{ color: '#0088CC', fontSize: 28 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Email">
              <IconButton onClick={openEmail}
                sx={{ backgroundColor: '#54657F15', '&:hover': { backgroundColor: '#54657F25' } }}>
                <EmailIcon sx={{ color: '#54657F', fontSize: 28 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy text">
              <IconButton onClick={handleCopyContent}
                sx={{ backgroundColor: '#1B365D15', '&:hover': { backgroundColor: '#1B365D25' } }}>
                <ContentCopyIcon sx={{ color: PRIMARY, fontSize: 28 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{selectedMaterial?.name}</Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMaterial?.imageUrl && (
            <Box sx={{ mb: 2, textAlign: 'center', backgroundColor: '#000', borderRadius: 2, overflow: 'hidden' }}>
              <img
                src={selectedMaterial.imageUrl}
                alt={selectedMaterial.name}
                style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }}
              />
            </Box>
          )}
          <Box sx={{
            p: 2, backgroundColor: '#F5F7FA', borderRadius: 2,
            whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem',
            maxHeight: 400, overflowY: 'auto',
          }}>
            {renderedContent || '(no content)'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
